// ============================================================
// Tiny Patio v2 — Shopify theme glue
// Cart drawer (optimistic), inline search, mobile nav, toast
// ============================================================
(function () {
  'use strict';

  const CART_URL = '/cart.js';
  const ADD_URL = '/cart/add.js';
  const CHANGE_URL = '/cart/change.js';
  const UPDATE_URL = '/cart/update.js';
  const SUGGEST_URL = '/search/suggest.json';

  const drawer = () => document.getElementById('basket-drawer');
  const scrim = () => document.getElementById('basket-scrim');

  // ─── Cached cart state ─────────────────────────────────────
  // Updated optimistically before the server confirms, so the UI
  // stays responsive even when clicks come in faster than the API.
  let cartCache = null;
  let renderToken = 0;          // bumps every time a fresh server response arrives
  const pendingByKey = new Map();   // key -> { qty, controller, timer, seq }
  let seqCounter = 0;

  // Whether the "Have a code?" field is expanded. Module-level because the drawer
  // footer is rebuilt from scratch on every cart change — a half-typed code must not
  // vanish because the shopper nudged a quantity.
  let discountFieldOpen = false;
  let discountBusy = false;

  // ─── Artist names for cart lines ───────────────────────────
  // /cart.js exposes `vendor` but no metafields, and the artist lives in the
  // `tinypatio.artist` metaobject list — so the drawer's "by …" line has to come from
  // Liquid. snippets/cart-artists-seed.liquid prints the map for the cart as it was on
  // page load; sections/cart-artists.liquid re-renders it on demand for handles added since.
  const artistByHandle = new Map();
  let artistFetch = null;

  function mergeArtistJSON(node) {
    if (!node) return;
    try {
      const map = JSON.parse(node.textContent || '{}');
      Object.keys(map).forEach(h => artistByHandle.set(h, map[h]));
    } catch (e) { /* malformed map — the "by …" line is simply left off */ }
  }
  mergeArtistJSON(document.querySelector('script[data-cart-artists]'));

  // Artist for a cart line. Nothing is printed until the map has the handle: `line.vendor`
  // is the shop name here, not the maker, so flashing it would show the wrong credit. The
  // seed covers the cart as it loaded; only a just-added product waits on the fetch below.
  function artistFor(line) {
    return (line.handle && artistByHandle.get(line.handle)) || '';
  }

  // Pull the map for handles we haven't seen yet, then patch the rendered lines in place.
  async function refreshCartArtists(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const missing = cart.items.some(l => l.handle && !artistByHandle.has(l.handle));
    if (!missing) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
    const url = root.replace(/\/$/, '') + '/?section_id=cart-artists';
    try {
      if (artistFetch) artistFetch.abort();
      artistFetch = new AbortController();
      const r = await fetch(url, { signal: artistFetch.signal, headers: { Accept: 'text/html' } });
      if (!r.ok) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = await r.text();
      mergeArtistJSON(tmp.querySelector('script[data-cart-artists]'));
    } catch (e) { return; /* aborted or offline — the line just stays without a credit */ }

    document.querySelectorAll('.basket-drawer .line[data-line-handle]').forEach(row => {
      const name = artistByHandle.get(row.getAttribute('data-line-handle'));
      if (!name) return;
      let by = row.querySelector('.line-body .by');
      if (by) { by.textContent = 'by ' + name; return; }
      const title = row.querySelector('.line-body .title');
      if (!title) return;
      by = document.createElement('p');
      by.className = 'by';
      by.textContent = 'by ' + name;
      title.insertAdjacentElement('afterend', by);
    });
  }

  // ─── Toast ────────────────────────────────────────────────
  let toastTimer;
  function toast(msg) {
    let el = document.getElementById('tp-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'tp-toast';
      el.className = 'toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    requestAnimationFrame(() => el.classList.add('show'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('show'), 2200);
  }

  // ─── Format money ─────────────────────────────────────────
  function formatMoney(cents) {
    const sym = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD') ? '$' : '£';
    return sym + (cents / 100).toFixed(2);
  }

  // ─── Drawer open/close ─────────────────────────────────────
  function openDrawer() {
    const d = drawer(); const s = scrim();
    if (!d || !s) return;
    d.classList.add('open');
    d.setAttribute('aria-hidden', 'false');
    s.classList.add('open');
    document.body.classList.add('drawer-open');
  }
  function closeDrawer() {
    const d = drawer(); const s = scrim();
    if (!d) return;
    d.classList.remove('open');
    d.setAttribute('aria-hidden', 'true');
    if (s) s.classList.remove('open');
    document.body.classList.remove('drawer-open');
  }

  // ─── Shipping line + free-shipping progress ────────────────
  // Three parts in one block: the shipping cost the customer will actually be
  // charged, the progress track, and the nudge to clear the threshold.
  //
  // Measured on total_price — AFTER any discount — on purpose. Shopify evaluates
  // price-based shipping rates on the discounted total and offers no setting to
  // change that, so a bar measured on the pre-discount basket would promise free
  // shipping that checkout then refuses. Better to say £3.95 here, where a shopper
  // can still do something about it, than to spring it on them at checkout.
  //
  // standard_shipping_rate is a hand-kept mirror of the Admin rate (see its setting
  // note) — the storefront has no way to price a rate without a delivery address.
  //
  // A free-shipping CODE is the exception the threshold can't see, and
  // hasFreeShippingCode() below explains why. It outranks the threshold: a shopper
  // who already has free delivery must never be told to spend more to get it.
  function shippingRowHTML(totalCents, cart) {
    const cfg = window.TP_CONFIG || {};
    const S = window.TP_STRINGS || {};
    const threshold = cfg.free_shipping_threshold_cents || 0;
    const rate = cfg.standard_shipping_rate_cents || 0;
    if (cfg.show_free_shipping_bar === false || threshold <= 0) return '';

    const byCode = hasFreeShippingCode(cart === undefined ? cartCache : cart);
    const qualified = byCode || totalCents >= threshold;
    const pct = byCode ? 100 : Math.max(0, Math.min(100, (totalCents / threshold) * 100));

    // One line, cost first: the fee is the number being decided about, and the
    // amount to clear it is the way out. "add £2.25 for free" on its own invites
    // "free what?", so the thing being made free is named once, in the same breath.
    let msg;
    if (byCode) {
      // Named differently from the threshold win so the shopper can tell which one
      // is paying, and notices if removing the code takes the delivery back.
      msg = S.shipping_free_code || S.shipping_free || 'Free shipping included';
    } else if (qualified) {
      msg = S.shipping_free || 'Free shipping included';
    } else {
      const remaining = formatMoney(threshold - totalCents);
      msg = rate > 0
        ? (S.shipping_with_fee || 'UK Delivery: {{ fee }} — Add {{ remaining }} for FREE shipping')
            .replace('{{ fee }}', formatMoney(rate)).replace('{{ remaining }}', remaining)
        : (S.free_shipping_progress || 'Add {{ remaining }} for FREE shipping')
            .replace('{{ remaining }}', remaining);
    }

    // The truck's own width (20px, see .ship-bar__truck) is discounted out of its
    // offset in proportion to how far along it is, so it never hangs off either end
    // of the track. Keep the 20 in step with the CSS if that width ever changes.
    const truckLeft = 'calc(' + pct.toFixed(1) + '% - ' + (pct * 0.20).toFixed(1) + 'px)';

    return (
      '<div class="ship-bar' + (qualified ? ' qualified' : '') + '">' +
        '<p class="ship-bar__msg">' + escapeHtml(msg) + '</p>' +
        '<div class="ship-bar__meter">' +
          '<div class="ship-bar__track">' +
            '<span class="ship-bar__fill" style="width:' + pct.toFixed(1) + '%"></span>' +
            '<span class="ship-bar__truck" style="left:' + truckLeft + '" aria-hidden="true"></span>' +
          '</div>' +
          '<span class="ship-bar__goal">' + escapeHtml(formatMoney(threshold).replace(/\.00$/, '')) + '</span>' +
        '</div>' +
      '</div>'
    );
  }

  // ─── Discount codes ────────────────────────────────────────
  // /cart/update.js grew a `discount` parameter in May 2025. The code is stored on
  // the cart itself and carried into checkout, so entering it here isn't a shortcut
  // for typing it at checkout — it is the same thing, just visible early enough to
  // matter.
  //
  // cart.discount_codes is the source of truth rather than
  // cart_level_discount_applications: a code that targets particular products is
  // allocated per line and never appears in the cart-level list.
  //
  // Verified against the live cart API, because none of this is documented: an
  // unusable code comes back HTTP 200, not an error, and Shopify KEEPS it on the
  // cart, flagged applicable:false and with no `amount` at all —
  //   {"code":"TP-NOT-A-REAL-CODE-XYZ","applicable":false}
  // Hence the filter: a mistyped code must never render as a pill, and must never
  // be re-sent on the next update.
  function appliedCodes(cart) {
    return (((cart || {}).discount_codes) || []).filter(d => d && d.code && d.applicable !== false);
  }

  // What each code actually took off. NOT in discount_codes — verified against the
  // live API with a real code, those entries carry only {code, applicable}:
  //   [{"code": "WHGT-TPU2-3WT4", "applicable": true}]
  // The money sits in the allocation objects instead, keyed by title, and the two
  // lists are NOT the same shape. Re-measured on the live cart API 2026-09-22 with a
  // 10%-off code on a £25 basket, because the nested form was assumed for both and
  // the cart-level branch had therefore been reading undefined all along:
  //   cart_level_discount_applications[] IS the application, flat —
  //     {title:"WHGT-TPU2-3WT4", target_type:"line_item", total_allocated_amount:250}
  //   items[].line_level_discount_allocations[] WRAPS one, for a code that targets
  //     particular products rather than the whole order —
  //     {amount:250, discount_application:{title,…}}
  // Summed across both, so a product-level code shows its value too. An order-level
  // code appears in the cart-level list only (its line_level_discount_allocations
  // came back []), so nothing is counted twice.
  function discountAmounts(cart) {
    const byCode = {};
    const add = alloc => {
      if (!alloc) return;
      const app = alloc.discount_application || alloc;
      const title = app.title;
      if (!title) return;
      const amount = (alloc.amount !== undefined) ? alloc.amount : app.total_allocated_amount;
      const key = String(title).toLowerCase();
      byCode[key] = (byCode[key] || 0) + (Number(amount) || 0);
    };
    ((cart || {}).cart_level_discount_applications || []).forEach(add);
    ((cart || {}).items || []).forEach(l => (l.line_level_discount_allocations || []).forEach(add));
    return byCode;
  }

  function hasCartDiscount(cart) {
    return appliedCodes(cart).length > 0 || !!(cart && cart.total_discount);
  }

  // A free-shipping code is invisible in the cart's money, which is why a bar
  // measured on total_price alone kept charging for delivery. Measured against the
  // live cart API on 2026-09-22, same £25 basket, one code at a time:
  //
  //                                   FREESHIP          WHGT-TPU2-3WT4 (10% off)
  //   total_price                         2500                          2250
  //   total_discount                         0                           250
  //   cart_level_discount_applications      []   [{ target_type: "line_item",
  //                                                 total_allocated_amount: 250 }]
  //   discount_codes            [{applicable:true}]          [{applicable:true}]
  //
  // So there is no target_type:"shipping_line" to look for. The cart has no shipping
  // line to allocate against — Shopify only resolves a shipping discount at checkout,
  // once an address exists — and it reports nothing in the meantime beyond having
  // accepted the code. The one signal left is the shape of that gap: a code Shopify
  // calls applicable that takes nothing off the goods must be paying for something
  // that isn't the goods, and delivery is the only other thing a code can pay for.
  //
  // Known blind spot: a Buy-X-get-Y code counted applicable before the free item is
  // in the basket would read as free shipping too. The shop runs none; revisit if one
  // is ever created.
  //
  // Liquid cannot run this check at all — it has no cart.discount_codes, and its
  // cart.discount_applications is the same empty array — so the server-rendered bar
  // in sections/main-cart.liquid stays threshold-only and init()'s renderCart()
  // repaints it a moment after load.
  function hasFreeShippingCode(cart) {
    if (!cart) return false;
    const amounts = discountAmounts(cart);
    return appliedCodes(cart).some(d => !(amounts[String(d.code).toLowerCase()] > 0));
  }

  function itemsSubtotal(cart) {
    if (!cart) return 0;
    if (typeof cart.items_subtotal_price === 'number') return cart.items_subtotal_price;
    return (cart.items || []).reduce((sum, l) => sum + (l.final_line_price || 0), 0);
  }

  // Chevron points UP because that is where the panel goes; CSS flips it when open.
  const CARET_SVG = '<svg class="cart-discount__caret" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">' +
    '<path d="M3 10l5-5 5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const TAG_SVG = '<svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">' +
    '<path d="M8.6 1.5H14v5.4L7.4 13.5a1.2 1.2 0 0 1-1.7 0L2.1 9.9a1.2 1.2 0 0 1 0-1.7L8.6 1.5Z" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/>' +
    '<circle cx="11" cy="4.6" r="1" fill="currentColor"/></svg>';

  function discountBlockHTML(cart) {
    const S = window.TP_STRINGS || {};
    const codes = appliedCodes(cart);
    const saved = Number(cart.total_discount) || 0;
    const open = discountFieldOpen;

    const amounts = discountAmounts(cart);
    const chips = codes.map(d => {
      const code = String(d.code);
      const amount = amounts[code.toLowerCase()] || 0;
      return '<div class="cart-discount__applied">' +
        '<span class="cart-discount__tag">' + escapeHtml(code) + '</span>' +
        '<span class="cart-discount__amount">' + (amount > 0 ? '−' + formatMoney(amount) : '') + '</span>' +
        '<button type="button" class="cart-discount__remove" data-discount-remove="' + escapeHtml(code) + '"' +
          ' aria-label="' + escapeHtml(S.discount_remove || 'Remove discount') + '">&times;</button>' +
      '</div>';
    }).join('');

    // Panel FIRST in the DOM, toggle second: the footer is anchored to the bottom of
    // the drawer, so opening the panel grows the block upward out of the button
    // instead of shoving Checkout down the screen.
    return '<div class="cart-discount" data-discount-block>' +
      '<div class="cart-discount__panel"' + (open ? '' : ' hidden') + ' data-discount-form>' +
        chips +
        '<div class="cart-discount__entry">' +
          '<input type="text" data-discount-input autocomplete="off" autocapitalize="characters" spellcheck="false"' +
            ' placeholder="' + escapeHtml(S.discount_placeholder || 'Discount code') + '">' +
          '<button type="button" data-discount-apply>' + escapeHtml(S.discount_apply || 'Apply') + '</button>' +
        '</div>' +
        '<p class="cart-discount__err" data-discount-error role="alert" hidden></p>' +
      '</div>' +
      '<button type="button" class="cart-discount__toggle" data-discount-toggle aria-expanded="' + (open ? 'true' : 'false') + '">' +
        CARET_SVG +
        '<span>' + escapeHtml(codes.length ? (S.discount_prompt_more || 'Add another code?') : (S.discount_prompt || 'Have a code?')) + '</span>' +
      '</button>' +
      // The running total of everything the codes took off, so the saving stays on
      // screen with the panel shut.
      (saved > 0
        ? '<div class="cart-discount__summary">' +
            TAG_SVG +
            '<span class="cart-discount__summary-label">' + escapeHtml(S.discount_saved || 'Discount') + '</span>' +
            '<span class="cart-discount__summary-value">−' + formatMoney(saved) + '</span>' +
          '</div>'
        : '') +
    '</div>';
  }

  function showDiscountError(message) {
    document.querySelectorAll('[data-discount-error]').forEach(el => {
      el.textContent = message || '';
      el.hidden = !message;
    });
  }

  // update.js replaces the whole set of codes, so every change re-sends the full list.
  async function setDiscounts(codes) {
    const r = await fetch(UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
    const body = await r.json().catch(() => null);
    if (!r.ok) throw new Error((body && (body.description || body.message)) || '');
    if (!body || !Array.isArray(body.items)) throw new Error('');
    // Re-read rather than trust the update.js body. Tested with a real code, update.js
    // DOES price the discount in immediately (£47.50 basket + 10% came back
    // total_price 4275), so this is belt and braces rather than a known fix — but a
    // reported case of the shipping row still reading "Free" right after a code was
    // applied was never reproduced, and one extra ~260ms read on a deliberate click is
    // cheap next to rendering a basket that is only half settled.
    const fresh = await fetch(CART_URL, { headers: { Accept: 'application/json' } })
      .then(x => x.json()).catch(() => null);
    return (fresh && Array.isArray(fresh.items)) ? fresh : body;
  }

  // The cart page has to reload rather than repaint: its section key is "main", so
  // the Section Rendering API returns nothing for it, and a product-level code
  // rewrites the per-line prices Liquid already printed.
  function commitCart(cart) {
    if (document.body.classList.contains('template-cart')) {
      window.location.reload();
      return;
    }
    renderCart(cart);
  }

  async function applyDiscount(code) {
    const entered = String(code || '').trim();
    const S = window.TP_STRINGS || {};
    if (!entered || discountBusy) return;
    discountBusy = true;
    showDiscountError('');
    const previous = appliedCodes(cartCache).map(d => String(d.code));
    try {
      let cart = await setDiscounts(previous.concat([entered]));
      const found = (cart.discount_codes || []).find(
        d => d && String(d.code).toLowerCase() === entered.toLowerCase()
      );
      if (!found || found.applicable === false) {
        // Rejected — but Shopify has still written it onto the cart, where it would
        // ride along to checkout and be refused there instead. Put the cart back the
        // way we found it. One message covers both a typo and a code whose rules this
        // basket doesn't meet: the API gives us no way to tell them apart.
        cart = await setDiscounts(previous).catch(() => cart);
        cartCache = cart;
        showDiscountError(S.discount_invalid || 'That code isn’t valid for this basket.');
        return;
      }
      cartCache = cart;
      discountFieldOpen = false;
      commitCart(cart);
    } catch (e) {
      showDiscountError((e && e.message) || S.discount_failed || 'Could not apply that code.');
    } finally {
      discountBusy = false;
    }
  }

  async function removeDiscount(code) {
    if (discountBusy) return;
    const target = String(code || '').toLowerCase();
    discountBusy = true;
    showDiscountError('');
    try {
      const keep = appliedCodes(cartCache)
        .map(d => String(d.code))
        .filter(c => c.toLowerCase() !== target);
      commitCart(await setDiscounts(keep));
    } catch (e) {
      showDiscountError((e && e.message) || (window.TP_STRINGS || {}).discount_failed || '');
    } finally {
      discountBusy = false;
    }
  }

  // ─── Upsell carousel (Section Rendering API) ───────────────
  // The upsell pool depends on the metafields of the products in the basket, which
  // /cart.js and /products/x.js don't expose. So the carousel is rendered server-side
  // by sections/cart-upsell.liquid and pulled in here whenever the cart changes. The
  // section already de-dupes, excludes in-cart items, and honours the limit; we just
  // drop its .tp-upsell node into every [data-upsell-slot] (drawer footer + cart page).
  let upsellAbort = null;
  let upsellTimer = null;
  function scheduleUpsell() {
    clearTimeout(upsellTimer);
    upsellTimer = setTimeout(refreshUpsell, 120);
  }
  async function refreshUpsell() {
    const slots = document.querySelectorAll('[data-upsell-slot]');
    if (!slots.length) return;
    if (window.TP_CONFIG && window.TP_CONFIG.cart_upsell_enabled === false) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
    const url = root.replace(/\/$/, '') + '/?section_id=cart-upsell';
    try {
      if (upsellAbort) upsellAbort.abort();
      upsellAbort = new AbortController();
      const r = await fetch(url, { signal: upsellAbort.signal, headers: { Accept: 'text/html' } });
      if (!r.ok) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = await r.text();
      const node = tmp.querySelector('.tp-upsell');
      const html = node ? node.outerHTML : '';
      slots.forEach(s => { s.innerHTML = html; });
    } catch (e) { /* aborted or offline — leave the current carousel in place */ }
  }

  // ─── Render cart drawer ────────────────────────────────────
  function renderCart(cart) {
    if (!cart) return;
    cartCache = cart;
    renderToken++;
    // Announced so sections can react to the cart without a second /cart.js of their
    // own — sections/discount-welcome.liquid listens for a code arriving.
    try { document.dispatchEvent(new CustomEvent('tp:cart', { detail: cart })); } catch (e) {}
    const body = document.getElementById('basket-body');
    const foot = document.getElementById('basket-footer');
    if (!body || !foot) return;

    // Header count badge
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      if (cart.item_count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });

    // Page-cart free-shipping bar (server-rendered host node, refresh on change)
    document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
      el.innerHTML = shippingRowHTML(cart.total_price, cart);
    });

    if (cart.items.length === 0) {
      body.innerHTML =
        '<div class="empty"><p>' + (window.TP_STRINGS.empty || 'Nothing here yet.') + '</p>' +
        '<small>' + (window.TP_STRINGS.empty_subtext || '') + '</small></div>';
      foot.innerHTML = '<a class="btn btn-block" href="/collections/the-collection">' +
        (window.TP_STRINGS.continue_shopping || 'Continue shopping') + '</a>';
      return;
    }

    body.innerHTML = cart.items.map(line => {
      const img = line.image
        ? '<img src="' + line.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') + '" alt="" loading="lazy">'
        : '';
      const safeKey = String(line.key).replace(/"/g, '&quot;');
      const hasVariants = !line.product_has_only_default_variant;
      const variantLine = (line.variant_title && line.variant_title !== 'Default Title')
        ? '<p class="variant">' + escapeHtml(line.variant_title) +
            (hasVariants && line.handle
              ? ' <button type="button" class="variant-change" data-variant-edit aria-label="Change variant">' +
                  (window.TP_STRINGS.change || 'Change') + '</button>'
              : '') +
          '</p>'
        : '';
      const handleAttr = line.handle ? ' data-line-handle="' + escapeHtml(line.handle) + '"' : '';
      const variantIdAttr = line.variant_id ? ' data-line-variant-id="' + line.variant_id + '"' : '';
      const decDisabled = line.quantity <= 1 ? ' disabled' : '';
      const incDisabled = line.quantity >= lineMaxQty(line) ? ' disabled' : '';
      return '<div class="line" data-line-key="' + safeKey + '"' + handleAttr + variantIdAttr + '>' +
        '<div class="thumb">' + img + '</div>' +
        '<div class="line-body">' +
          '<p class="title">' + line.product_title + '</p>' +
          (artistFor(line) ? '<p class="by">by ' + escapeHtml(artistFor(line)) + '</p>' : '') +
          variantLine +
          '<div class="variant-editor" data-variant-editor hidden></div>' +
          '<span class="qty">' +
            '<button type="button" data-qty-dec aria-label="Decrease"' + decDisabled + '>−</button>' +
            '<span data-qty>' + line.quantity + '</span>' +
            '<button type="button" data-qty-inc aria-label="Increase"' + incDisabled + '>+</button>' +
          '</span>' +
          '<button type="button" class="remove" data-line-remove>' +
            (window.TP_STRINGS.remove || 'Remove') + '</button>' +
        '</div>' +
        '<span class="price" data-line-price>' + formatMoney(line.final_line_price) + '</span>' +
      '</div>';
    }).join('') +
      // The cross-sell rides at the end of the scrolling list, not in the sticky
      // footer: the footer is the most expensive space in the drawer and Checkout
      // has first claim on it.
      '<div data-upsell-slot></div>';

    // Subtotal is what the basket costs AFTER any code, matching what checkout will
    // charge. The saving isn't double-counted: each code carries its own value on its
    // chip above, which reads as a receipt line rather than a second subtotal. No
    // grand Total — shipping is an estimate (Tracked 48 vs 24) and a Total would read
    // as a promise.
    foot.innerHTML =
      discountBlockHTML(cart) +
      '<div class="row">' +
        '<span class="label">' + (window.TP_STRINGS.subtotal || 'Subtotal') + '</span>' +
        '<span class="value" data-cart-subtotal>' + formatMoney(cart.total_price) + '</span>' +
      '</div>' +
      shippingRowHTML(cart.total_price, cart) +
      '<a class="btn btn-block" href="/checkout">' + (window.TP_STRINGS.checkout || 'Checkout') + '</a>';

    // Load product JSON for every line: pre-warms the variant editor for multi-variant
    // items (so "Change" opens instantly) and supplies stock data so the + button can be
    // capped at available inventory once the JSON arrives.
    cart.items.forEach(line => {
      if (!line.handle) return;
      loadProductJson(line.handle).then(() => {
        const row = document.querySelector('.basket-drawer .line[data-line-key="' + CSS.escape(String(line.key)) + '"]');
        if (row) applyQtyLimits(row, line);
      });
    });

    // Refresh the cart-aware upsell carousel (debounced) now the cart has changed.
    scheduleUpsell();

    // Fill in the "by …" line for any product whose artist we don't have yet.
    refreshCartArtists(cart);
  }

  // ─── Optimistic local update ───────────────────────────────
  // Updates DOM right away. Recomputes line price from unit price * qty,
  // recomputes subtotal, and refreshes the free-shipping bar.
  function optimisticUpdate(key, newQty) {
    if (!cartCache || !Array.isArray(cartCache.items)) return;
    const line = cartCache.items.find(l => String(l.key) === String(key));
    if (!line) return;
    const unit = line.final_price; // per-unit after line discounts
    line.quantity = newQty;
    line.final_line_price = unit * newQty;

    cartCache.item_count = cartCache.items.reduce((s, l) => s + (l === line ? newQty : l.quantity), 0);
    const subtotal = cartCache.items.reduce((s, l) => s + l.final_line_price, 0);
    cartCache.items_subtotal_price = subtotal;
    // With a code on the cart, total_price is not the sum of the lines and a
    // percentage can't be extrapolated from here. Leave the shipping row to the
    // server answer ~220ms later rather than flash a number that is about to change.
    const discounted = hasCartDiscount(cartCache);
    if (!discounted) cartCache.total_price = subtotal;

    // Targeted DOM patches (no full innerHTML rebuild → no flicker, no scroll jump)
    const row = document.querySelector('.basket-drawer .line[data-line-key="' + CSS.escape(String(key)) + '"]');
    if (row) {
      const qtyEl = row.querySelector('[data-qty]');
      const priceEl = row.querySelector('[data-line-price]');
      if (qtyEl) qtyEl.textContent = newQty;
      if (priceEl) priceEl.textContent = formatMoney(line.final_line_price);
      if (newQty === 0) row.classList.add('removing');
      else applyQtyLimits(row, line);
    }
    // Subtotal is post-discount now, so with a code on the cart it can't be derived
    // from the lines either — same reasoning as the shipping row below.
    const sub = discounted ? null : document.querySelector('[data-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(subtotal);
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cartCache.item_count;
      if (cartCache.item_count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    // Refresh shipping rows (drawer + page)
    if (!discounted) {
      document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
        el.innerHTML = shippingRowHTML(cartCache.total_price);
      });
      const drawerBar = document.querySelector('#basket-footer .ship-bar');
      if (drawerBar) drawerBar.outerHTML = shippingRowHTML(cartCache.total_price);
    }
  }

  // ─── Debounced server sync ────────────────────────────────
  // Coalesce rapid clicks into one request per line. Latest qty wins.
  function scheduleChange(key) {
    const entry = pendingByKey.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => flushChange(key), 220);
  }

  async function flushChange(key) {
    const entry = pendingByKey.get(key);
    if (!entry) return;
    // Cancel any in-flight request for this key
    if (entry.controller) entry.controller.abort();
    const controller = new AbortController();
    entry.controller = controller;
    const sentQty = entry.qty;
    const mySeq = ++seqCounter;
    entry.seq = mySeq;

    let cart = null;
    let needsRecovery = false;

    try {
      const r = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: sentQty }),
        signal: controller.signal,
      });
      cart = await r.json().catch(() => null);
      if (!r.ok || !cart || !Array.isArray(cart.items)) needsRecovery = true;
    } catch (e) {
      if (e.name === 'AbortError') return; // superseded
      needsRecovery = true;
    }

    // Has a newer flushChange started? If so, bow out — let the newer one render.
    let cur = pendingByKey.get(key);
    if (!cur || cur.seq !== mySeq) return;

    // Server rejected the change or network failed. Refetch the authoritative cart
    // so cartCache never gets poisoned by a non-cart error response.
    if (needsRecovery) {
      try {
        const fresh = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
        const freshCart = await fresh.json();
        if (freshCart && Array.isArray(freshCart.items)) {
          cart = freshCart;
        } else {
          toast('Could not update basket.');
          return;
        }
      } catch (_) {
        toast('Could not update basket.');
        return;
      }
      // Re-check supersession after the recovery await.
      cur = pendingByKey.get(key);
      if (!cur || cur.seq !== mySeq) return;
    }

    // If the user clicked more while this request was in flight, their newer intent
    // (cur.qty) differs from what we just sent. Don't clobber the optimistic DOM with
    // stale server data and don't drop the pending entry — the scheduled next flush
    // will reconcile.
    if (cur.qty !== sentQty) return;

    pendingByKey.delete(key);
    renderCart(cart);
  }

  function changeLineQty(key, delta) {
    if (!cartCache || !Array.isArray(cartCache.items)) return;
    const line = cartCache.items.find(l => String(l.key) === String(key));
    if (!line) return;
    const existing = pendingByKey.get(key);
    const baseQty = existing ? existing.qty : line.quantity;
    // Stepper can't drop below 1 (Remove handles deletion) or exceed available stock.
    const newQty = Math.min(Math.max(1, baseQty + delta), lineMaxQty(line));
    if (newQty === baseQty) return;   // already at a limit — nothing to send
    pendingByKey.set(key, {
      qty: newQty,
      controller: existing ? existing.controller : null,
      timer: existing ? existing.timer : null,
      seq: existing ? existing.seq : 0,
    });
    optimisticUpdate(key, newQty);
    scheduleChange(key);
  }
  function removeLine(key) {
    if (!cartCache || !Array.isArray(cartCache.items)) return;
    const existing = pendingByKey.get(key) || {};
    pendingByKey.set(key, { qty: 0, controller: existing.controller || null, timer: existing.timer || null, seq: existing.seq || 0 });
    optimisticUpdate(key, 0);
    scheduleChange(key);
  }

  // ─── In-line variant switcher (cart drawer) ───────────────
  const productJsCache = new Map();      // handle -> Promise<product|null>
  const resolvedProducts = new Map();    // handle -> product|null (sync access once loaded)
  async function loadProductJson(handle) {
    if (productJsCache.has(handle)) return productJsCache.get(handle);
    const p = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    productJsCache.set(handle, p);
    p.then(prod => resolvedProducts.set(handle, prod || null));
    return p;
  }

  // Largest quantity a cart line may reach. Infinity when the variant isn't
  // inventory-tracked or allows overselling, or while product data is still loading
  // (the server clamps in that brief window; we re-cap once the JSON arrives).
  function lineMaxQty(line) {
    if (!line || !line.handle) return Infinity;
    const prod = resolvedProducts.get(line.handle);
    if (!prod || !Array.isArray(prod.variants)) return Infinity;
    const v = prod.variants.find(vv => vv.id === line.variant_id);
    if (!v) return Infinity;
    if (!v.inventory_management || v.inventory_policy === 'continue') return Infinity;
    if (typeof v.inventory_quantity !== 'number') return Infinity;
    return Math.max(0, v.inventory_quantity);
  }

  // Toggle a line's − / + buttons: no decreasing below 1 (use Remove instead),
  // no increasing past available stock.
  function applyQtyLimits(row, line) {
    if (!row || !line) return;
    const max = lineMaxQty(line);
    const dec = row.querySelector('[data-qty-dec]');
    const inc = row.querySelector('[data-qty-inc]');
    if (dec) dec.disabled = line.quantity <= 1;
    if (inc) inc.disabled = line.quantity >= max;
  }

  function renderVariantEditor(editor, product, selected, currentVariantId) {
    const findVariant = opts => product.variants.find(v => v.options.every((val, i) => val === opts[i])) || null;
    const isAvailable = (i, val) => product.variants.some(v =>
      v.available && v.options[i] === val && v.options.every((o, j) => j === i || o === selected[j])
    );
    const optionsHtml = product.options.map((opt, i) => {
      const optName = (opt && typeof opt === 'object') ? (opt.name || '') : opt;
      const seen = new Set();
      const values = [];
      product.variants.forEach(v => {
        const val = v.options[i];
        if (!seen.has(val)) { seen.add(val); values.push(val); }
      });
      const valsHtml = values.map(val => {
        const pressed = selected[i] === val;
        const unavail = !isAvailable(i, val);
        return '<button type="button" class="val' + (unavail ? ' unavailable' : '') + '"' +
          ' data-line-opt="' + i + '" data-line-val="' + escapeHtml(val) + '"' +
          ' aria-pressed="' + (pressed ? 'true' : 'false') + '">' + escapeHtml(val) + '</button>';
      }).join('');
      return '<div class="variant-editor-option">' +
        '<span class="label">' + escapeHtml(optName) + '</span>' +
        '<div class="values">' + valsHtml + '</div>' +
      '</div>';
    }).join('');
    const candidate = findVariant(selected);
    const sameAsCurrent = candidate && candidate.id === currentVariantId;
    const canConfirm = candidate && candidate.available && !sameAsCurrent;
    const actionsHtml =
      '<div class="variant-editor-actions">' +
        '<button type="button" class="variant-editor-cancel" data-variant-cancel>' +
          (window.TP_STRINGS.cancel || 'Cancel') + '</button>' +
        '<button type="button" class="variant-editor-confirm" data-variant-confirm' +
          (canConfirm ? '' : ' disabled') + '>' +
          (window.TP_STRINGS.update || 'Update') + '</button>' +
      '</div>';
    editor.innerHTML = optionsHtml + actionsHtml;
    editor.dataset.currentVariantId = currentVariantId;
    editor._product = product;
    editor._selected = selected;
  }

  // Preview a candidate variant in the cart drawer without mutating cartCache.
  // Updates: variant text, line image, line price, cart subtotal, free-shipping bar.
  function applyVariantPreview(row, variant) {
    const key = row.dataset.lineKey;
    const cachedLine = cartCache && cartCache.items.find(l => String(l.key) === String(key));
    if (!cachedLine || !variant) return;
    const variantP = row.querySelector('.variant');
    if (variantP) {
      const first = variantP.firstChild;
      if (first && first.nodeType === 3) first.nodeValue = (variant.title || '') + ' ';
      else variantP.insertBefore(document.createTextNode((variant.title || '') + ' '), variantP.firstChild);
    }
    const newLineTotal = variant.price * cachedLine.quantity;
    const newSubtotal = itemsSubtotal(cartCache) - cachedLine.final_line_price + newLineTotal;
    const priceEl = row.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = formatMoney(newLineTotal);
    const imgEl = row.querySelector('.thumb img');
    if (imgEl && variant.featured_image && variant.featured_image.src) {
      imgEl.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
    }
    // Same reasoning as optimisticUpdate: a preview can't price a cart-level code.
    if (!hasCartDiscount(cartCache)) {
      const sub = document.querySelector('[data-cart-subtotal]');
      if (sub) sub.textContent = formatMoney(newSubtotal);
      const drawerBar = document.querySelector('#basket-footer .ship-bar');
      if (drawerBar) drawerBar.outerHTML = shippingRowHTML(newSubtotal);
      document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
        el.innerHTML = shippingRowHTML(newSubtotal);
      });
    }
  }

  // Repaint a line + cart totals from the unmutated cartCache, discarding any preview.
  function restoreLineFromCache(row) {
    const key = row.dataset.lineKey;
    const cachedLine = cartCache && cartCache.items.find(l => String(l.key) === String(key));
    if (!cachedLine) return;
    const variantP = row.querySelector('.variant');
    if (variantP) {
      const first = variantP.firstChild;
      const t = (cachedLine.variant_title && cachedLine.variant_title !== 'Default Title') ? cachedLine.variant_title + ' ' : '';
      if (first && first.nodeType === 3) first.nodeValue = t;
    }
    const priceEl = row.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = formatMoney(cachedLine.final_line_price);
    const imgEl = row.querySelector('.thumb img');
    if (imgEl && cachedLine.image) imgEl.src = cachedLine.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
    const sub = document.querySelector('[data-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(cartCache.total_price);
    const drawerBar = document.querySelector('#basket-footer .ship-bar');
    if (drawerBar) drawerBar.outerHTML = shippingRowHTML(cartCache.total_price);
    document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
      el.innerHTML = shippingRowHTML(cartCache.total_price);
    });
  }

  async function openLineVariantEditor(row) {
    const editor = row.querySelector('[data-variant-editor]');
    if (!editor) return;
    const handle = row.dataset.lineHandle;
    if (!handle) return;
    if (!editor.hidden) {
      restoreLineFromCache(row);
      editor.hidden = true;
      editor.innerHTML = '';
      return;
    }
    editor.hidden = false;
    editor.innerHTML = '<div class="variant-editor-loading">' +
      (window.TP_STRINGS.loading || 'Loading…') + '</div>';
    const product = await loadProductJson(handle);
    if (!product) {
      editor.innerHTML = '<p class="variant-editor-error">' +
        (window.TP_STRINGS.variant_load_error || 'Could not load variants.') + '</p>';
      return;
    }
    const currentVariantId = parseInt(row.dataset.lineVariantId, 10);
    const currentVariant = product.variants.find(v => v.id === currentVariantId) || product.variants[0];
    const selected = currentVariant ? currentVariant.options.slice() : [];
    renderVariantEditor(editor, product, selected, currentVariantId);
  }

  async function switchLineVariant(key, newVariantId) {
    const line = cartCache && cartCache.items.find(l => String(l.key) === String(key));
    if (!line) return;
    const qty = line.quantity;
    const oldIndex = cartCache.items.indexOf(line);
    const preKeys = new Set(cartCache.items.map(l => String(l.key)));
    try {
      const r1 = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      if (!r1.ok) throw new Error('remove failed');
      const fd = new FormData();
      fd.append('id', newVariantId);
      fd.append('quantity', qty);
      const r2 = await fetch(ADD_URL, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!r2.ok) {
        const err = await r2.json().catch(() => ({}));
        toast(err.description || 'Could not update.');
      }
    } catch (e) {
      toast('Could not update.');
    }
    const cart = await fetchCart();
    // Preserve line position: locate the resulting line for newVariantId and put it where
    // the old one was. Prefer a brand-new key (the just-added line); otherwise fall back
    // to any matching variant_id (a merge into an existing line).
    if (cart && Array.isArray(cart.items) && oldIndex >= 0) {
      let idx = cart.items.findIndex(l => l.variant_id === newVariantId && !preKeys.has(String(l.key)));
      if (idx < 0) idx = cart.items.findIndex(l => l.variant_id === newVariantId);
      if (idx >= 0 && idx !== oldIndex) {
        const [item] = cart.items.splice(idx, 1);
        cart.items.splice(Math.min(oldIndex, cart.items.length), 0, item);
      }
    }
    renderCart(cart);
  }

  async function fetchCart() {
    const r = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  // ─── Add to cart ──────────────────────────────────────────
  async function addToCart(form) {
    const fromQuickAdd = !!form.closest('#quick-add-drawer');
    const data = new FormData(form);
    const btn = form.querySelector('[type="submit"], button:not([type])');
    // innerHTML, not textContent: the PDP button keeps its price in a span that phones hide,
    // and a textContent round-trip would flatten it and bring the price back after one add.
    if (btn) { btn.dataset.origHtml = btn.innerHTML; btn.disabled = true; btn.textContent = '…'; }
    try {
      const r = await fetch(ADD_URL, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || 'Could not add to basket.');
        return;
      }
      const cart = await fetchCart();
      renderCart(cart);
      toast(window.TP_STRINGS.added || 'Added to basket.');
      // On the cart page the line items are server-rendered, so reload to reflect
      // the new line + totals rather than opening the (redundant) drawer over it.
      if (document.body.classList.contains('template-cart')) {
        if (fromQuickAdd) closeQuickAdd();
        window.location.reload();
        return;
      }
      if (fromQuickAdd) {
        closeQuickAdd();
        openDrawer();
      } else if (window.TP_CONFIG && window.TP_CONFIG.cart_drawer_open_on_add) {
        openDrawer();
      }
    } catch (e) {
      toast('Connection error.');
    } finally {
      if (btn) { btn.disabled = false; if (btn.dataset.origHtml) btn.innerHTML = btn.dataset.origHtml; }
    }
  }

  // ─── Upsell add-to-basket ──────────────────────────────────
  // Single-variant products are added straight to the basket; multi-variant
  // products open the quick-add drawer so the customer can pick options first.
  async function upsellAdd(btn) {
    const hasVariants = btn.dataset.upsellHasVariants === 'true';
    const handle = btn.dataset.upsellHandle;
    if (hasVariants) {
      if (handle) { openQuickAdd(); loadQuickAdd(handle); }
      return;
    }
    const variantId = btn.dataset.upsellVariantId;
    if (!variantId) return;
    const orig = btn.textContent;
    btn.disabled = true; btn.textContent = '…';
    try {
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', 1);
      const r = await fetch(ADD_URL, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || 'Could not add to basket.');
        return;
      }
      const cart = await fetchCart();
      renderCart(cart);
      toast(window.TP_STRINGS.added || 'Added to basket.');
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
    } catch (e) {
      toast('Connection error.');
    } finally {
      btn.disabled = false; btn.textContent = orig;
    }
  }

  // ─── Add multiple items at once ────────────────────────────
  // Used by the PDP "often bought together" bundle: posts an items[]
  // array to /cart/add.js in a single request, then refreshes the cart
  // UI (which also refreshes the upsell carousel) and opens the drawer.
  async function addItemsToCart(items, btn) {
    if (!items || !items.length) return;
    if (btn) { btn.dataset.origText = btn.textContent; btn.disabled = true; btn.textContent = '…'; }
    try {
      const r = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || 'Could not add to basket.');
        return;
      }
      const cart = await fetchCart();
      renderCart(cart);
      toast(window.TP_STRINGS.added || 'Added to basket.');
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      openDrawer();
    } catch (e) {
      toast('Connection error.');
    } finally {
      if (btn) { btn.disabled = false; if (btn.dataset.origText) btn.textContent = btn.dataset.origText; }
    }
  }

  // ─── Quick-add drawer ─────────────────────────────────────
  // Opens a side drawer with the product's variant pickers when a
  // collection card's "Choose options" CTA is clicked. Submits via
  // the standard data-cart-add path; on success the quick-add closes
  // and the cart drawer opens for confirmation.
  const qaDrawer = () => document.getElementById('quick-add-drawer');
  const qaScrim = () => document.getElementById('quick-add-scrim');
  let qaProduct = null;
  let qaSelected = [];
  // Metafield-backed extras handed over by the trigger element. /products/<handle>.js
  // returns no metafields, so artist name and rating can only reach the popup this way —
  // the Liquid that rendered the card puts them on the link as data-*.
  let qaMeta = { shortDesc: '', artist: '', rating: null, ratingMax: 5, ratingCount: 0 };

  function openQuickAdd() {
    const d = qaDrawer(); const s = qaScrim();
    if (!d || !s) return;
    d.classList.add('open');
    d.setAttribute('aria-hidden', 'false');
    s.classList.add('open');
    document.body.classList.add('drawer-open');
  }
  function closeQuickAdd() {
    const d = qaDrawer(); const s = qaScrim();
    if (!d) return;
    d.classList.remove('open');
    d.setAttribute('aria-hidden', 'true');
    if (s) s.classList.remove('open');
    // Release body scroll lock only if the cart drawer isn't also open
    const cd = drawer();
    if (!cd || !cd.classList.contains('open')) {
      document.body.classList.remove('drawer-open');
    }
  }

  async function loadQuickAdd(handle, data) {
    const body = document.getElementById('quick-add-body');
    if (!body) return;
    const d = data || {};
    const rating = parseFloat(d.rating);
    const count = parseInt(d.ratingCount, 10);
    qaMeta = {
      shortDesc: d.shortDesc || '',
      artist: d.artist || '',
      rating: isNaN(rating) ? null : rating,
      ratingMax: parseFloat(d.ratingMax) || 5,
      ratingCount: isNaN(count) ? 0 : count
    };
    body.innerHTML = '<div class="quick-add-loading">Loading…</div>';
    try {
      const r = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error('Fetch failed');
      const product = await r.json();
      qaProduct = product;
      const initial = product.variants.find(v => v.available) || product.variants[0];
      qaSelected = initial ? initial.options.slice() : [];
      renderQuickAdd();
    } catch (e) {
      body.innerHTML = '<div class="quick-add-error">Could not load product. Please try again.</div>';
    }
  }

  function findQaVariant(opts) {
    if (!qaProduct) return null;
    return qaProduct.variants.find(v => v.options.every((val, i) => val === opts[i])) || null;
  }
  // Is there an available variant with `value` at `optionIndex`, given the other currently-selected options?
  function isQaValueAvailable(optionIndex, value) {
    if (!qaProduct) return false;
    return qaProduct.variants.some(v => {
      if (!v.available) return false;
      if (v.options[optionIndex] !== value) return false;
      return v.options.every((val, i) => i === optionIndex || val === qaSelected[i]);
    });
  }

  function renderQuickAdd() {
    const body = document.getElementById('quick-add-body');
    if (!body || !qaProduct) return;
    const variant = findQaVariant(qaSelected);
    const variantImg = variant && variant.featured_image && variant.featured_image.src;
    const featured = variantImg || qaProduct.featured_image || '';
    const imgUrl = featured ? (featured.indexOf('//') === 0 ? 'https:' + featured : featured) : '';
    const imgSized = imgUrl ? imgUrl.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';

    const heroHtml =
      '<div class="quick-add-hero">' +
        '<div class="thumb">' + (imgSized ? '<img src="' + imgSized + '" alt="" loading="lazy">' : '') + '</div>' +
        '<div>' +
          '<p class="title">' + escapeHtml(qaProduct.title) + '</p>' +
          (qaMeta.artist ? '<p class="artist">by ' + escapeHtml(qaMeta.artist) + '</p>' : '') +
          '<p class="price">' +
            (variant ? formatMoney(variant.price) : formatMoney(qaProduct.price)) +
            (variant && variant.compare_at_price && variant.compare_at_price > variant.price
              ? '<span class="compare">' + formatMoney(variant.compare_at_price) + '</span>' : '') +
          '</p>' +
          starsHtml(qaMeta.rating, qaMeta.ratingMax, qaMeta.ratingCount) +
        '</div>' +
      '</div>';

    // Single-variant ("simple") products have no real options — Shopify reports a
    // lone "Title / Default Title" variant. Skip the picker so the popup reads as a
    // clean quick-view + add rather than a pointless one-value option row.
    const firstOptName = qaProduct.options.length
      ? ((typeof qaProduct.options[0] === 'object') ? qaProduct.options[0].name : qaProduct.options[0])
      : '';
    const isDefaultOnly = qaProduct.variants.length === 1 &&
      (qaProduct.variants[0].title === 'Default Title' || firstOptName === 'Title');

    const optionsHtml = isDefaultOnly ? '' : qaProduct.options.map((opt, i) => {
      const optName = (opt && typeof opt === 'object') ? (opt.name || '') : opt;
      const seen = new Set();
      const values = [];
      qaProduct.variants.forEach(v => {
        const val = v.options[i];
        if (!seen.has(val)) { seen.add(val); values.push(val); }
      });
      const valuesHtml = values.map(val => {
        const pressed = qaSelected[i] === val;
        const unavailable = !isQaValueAvailable(i, val);
        return '<button type="button" class="val' + (unavailable ? ' unavailable' : '') + '"' +
          ' data-qa-option="' + i + '" data-qa-value="' + escapeHtml(val) + '"' +
          ' aria-pressed="' + (pressed ? 'true' : 'false') + '">' + escapeHtml(val) + '</button>';
      }).join('');
      return '<div class="quick-add-option">' +
        '<span class="label">' + escapeHtml(optName) +
          (qaSelected[i] ? '<span class="selected">' + escapeHtml(qaSelected[i]) + '</span>' : '') +
        '</span>' +
        '<div class="values">' + valuesHtml + '</div>' +
      '</div>';
    }).join('');

    const available = !!(variant && variant.available);
    const btnLabel = !variant ? 'Unavailable' :
      (available ? (window.TP_STRINGS.add_to_cart || 'Add to basket') : (window.TP_STRINGS.sold_out || 'Sold out'));
    const stockText = !variant ? 'Unavailable' :
      (available ? (window.TP_STRINGS.in_stock || 'In stock') : (window.TP_STRINGS.sold_out || 'Sold out'));

    const footerHtml =
      '<div class="quick-add-footer">' +
        '<div class="stock-row' + (available ? '' : ' out') + '">' +
          '<span><span class="dot"></span>' + stockText + '</span>' +
          '<a class="view-full" href="' + qaProduct.url + '">View full details →</a>' +
        '</div>' +
        '<form data-cart-add method="post" action="/cart/add">' +
          '<input type="hidden" name="id" value="' + (variant ? variant.id : '') + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="submit" class="btn-add"' + (available ? '' : ' disabled') + '>' + btnLabel + '</button>' +
        '</form>' +
      '</div>';

    // Short blurb: prefer the value passed in from the card (tinypatio.short_description);
    // otherwise derive one from the full description so every popup gets a blurb.
    const blurb = qaMeta.shortDesc || plainText(qaProduct.description, 30);
    const descHtml = blurb ? '<p class="quick-add-desc">' + escapeHtml(blurb) + '</p>' : '';

    body.innerHTML = heroHtml + descHtml + optionsHtml + footerHtml;
  }

  // ─── Mobile nav drawer ────────────────────────────────────
  function openMobileNav() {
    const d = document.getElementById('MobileNav');
    const s = document.querySelector('.mobile-nav-scrim');
    const t = document.querySelector('[data-mobile-nav-open]');
    if (!d || !s) return;
    s.hidden = false;
    requestAnimationFrame(() => { d.classList.add('open'); s.classList.add('open'); });
    d.setAttribute('aria-hidden', 'false');
    if (t) t.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
  }
  function closeMobileNav() {
    const d = document.getElementById('MobileNav');
    const s = document.querySelector('.mobile-nav-scrim');
    const t = document.querySelector('[data-mobile-nav-open]');
    if (!d || !s) return;
    d.classList.remove('open'); s.classList.remove('open');
    d.setAttribute('aria-hidden', 'true');
    if (t) t.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
    setTimeout(() => { s.hidden = true; }, 280);
  }

  // ─── Desktop nav dropdown ─────────────────────────────────
  // Chuột thì CSS :hover lo hết, bàn phím thì CSS :focus-within lo hết. JS chỉ cần cho màn
  // >=1151px KHÔNG có hover (iPad ngang): tap lần đầu mở panel, tap lần hai mới đi tới link cha.
  function closeNavDropdowns() {
    document.querySelectorAll('.tp-nav__item--has-children.is-open')
      .forEach(item => item.classList.remove('is-open'));
  }

  // ─── Inline predictive search ─────────────────────────────
  let searchAbort = null;
  let searchTimer = null;

  function openSearch() {
    const panel = document.getElementById('tp-search-panel');
    if (!panel) return;
    panel.classList.add('open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    const input = panel.querySelector('input[type="search"]');
    if (input) setTimeout(() => input.focus(), 80);
  }
  function closeSearch() {
    const panel = document.getElementById('tp-search-panel');
    if (!panel) return;
    panel.classList.remove('open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-open');
    if (searchAbort) searchAbort.abort();
  }
  async function runSearch(q) {
    const results = document.getElementById('tp-search-results');
    if (!results) return;
    const trimmed = (q || '').trim();
    if (trimmed.length < 2) {
      results.innerHTML = '';
      return;
    }
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    results.classList.add('loading');
    try {
      const url = SUGGEST_URL + '?q=' + encodeURIComponent(trimmed) +
        '&resources[type]=product,article,page&resources[limit]=6&resources[options][unavailable_products]=last';
      const r = await fetch(url, { signal: searchAbort.signal, headers: { Accept: 'application/json' } });
      const data = await r.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
      const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];
      if (!products.length && !articles.length && !pages.length) {
        results.innerHTML = '<p class="tp-search-empty">' +
          (window.TP_STRINGS.search_empty || 'No matches for') + ' "<strong>' + escapeHtml(trimmed) + '</strong>"</p>';
      } else {
        let html = '';
        if (products.length) {
          html += '<h4 class="tp-search-group">' + (window.TP_STRINGS.search_products || 'Products') + '</h4><ul class="tp-search-list">';
          html += products.map(p => {
            const img = p.featured_image && p.featured_image.url
              ? '<img src="' + p.featured_image.url + '&width=120" alt="" loading="lazy">'
              : '<span class="tp-search-thumb-placeholder"></span>';
            const price = p.price ? '<span class="tp-search-price">' + p.price + '</span>' : '';
            return '<li><a href="' + p.url + '">' +
              '<span class="tp-search-thumb">' + img + '</span>' +
              '<span class="tp-search-text"><span class="tp-search-title">' + escapeHtml(p.title) + '</span>' + price + '</span>' +
              '</a></li>';
          }).join('');
          html += '</ul>';
        }
        if (articles.length) {
          html += '<h4 class="tp-search-group">' + (window.TP_STRINGS.search_articles || 'Journal') + '</h4><ul class="tp-search-list tp-search-list--text">';
          html += articles.map(a => '<li><a href="' + a.url + '">' + escapeHtml(a.title) + '</a></li>').join('');
          html += '</ul>';
        }
        if (pages.length) {
          html += '<h4 class="tp-search-group">' + (window.TP_STRINGS.search_pages || 'Pages') + '</h4><ul class="tp-search-list tp-search-list--text">';
          html += pages.map(p => '<li><a href="' + p.url + '">' + escapeHtml(p.title) + '</a></li>').join('');
          html += '</ul>';
        }
        html += '<a class="tp-search-all" href="/search?q=' + encodeURIComponent(trimmed) + '">' +
          (window.TP_STRINGS.search_all || 'View all results') + ' →</a>';
        results.innerHTML = html;
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        results.innerHTML = '<p class="tp-search-empty">Search unavailable. Try again.</p>';
      }
    } finally {
      results.classList.remove('loading');
    }
  }
  // Star rating row. Must stay byte-comparable with snippets/star-rating.liquid so a card
  // and the popup it opens never render the same product's stars differently.
  // Returns '' for an unrated product, so callers can concatenate it unconditionally.
  function starsHtml(rating, max, count) {
    if (rating == null || !(count > 0)) return '';
    const scale = max || 5;
    const pct = Math.round(Math.max(0, Math.min(rating / scale, 1)) * 1000) / 10;
    const label = 'Rated ' + rating.toFixed(1) + ' out of ' + Math.round(scale) +
      ' from ' + count + ' review' + (count === 1 ? '' : 's');
    return '<span class="tp-stars" role="img" aria-label="' + escapeHtml(label) + '">' +
      '<span class="tp-stars__bar" aria-hidden="true"><span class="tp-stars__on" style="width: ' + pct + '%;"></span></span>' +
      '<span class="tp-stars__n" aria-hidden="true">(' + count + ')</span>' +
    '</span>';
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }
  // Strip HTML and trim to `words` words (with an ellipsis) — used to derive a
  // short blurb from a product's full description when no short_description is given.
  function plainText(html, words) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    if (!words) return text;
    const parts = text.split(' ');
    return parts.length > words ? parts.slice(0, words).join(' ') + '…' : text;
  }

  // ─── Init ──────────────────────────────────────────────────
  function init() {
    // Cart open — open instantly using cached state, refresh in background
    document.querySelectorAll('[data-cart-open]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        if (cartCache) renderCart(cartCache); // paint with what we have
        openDrawer();                          // open immediately
        fetchCart().then(renderCart).catch(() => {});
      });
    });
    // Cart close
    document.querySelectorAll('[data-cart-close]').forEach(el => el.addEventListener('click', closeDrawer));
    if (scrim()) scrim().addEventListener('click', closeDrawer);

    // Delegated handlers so re-rendered DOM nodes keep working
    const bodyEl = document.getElementById('basket-body');
    if (bodyEl) {
      bodyEl.addEventListener('click', e => {
        const incBtn = e.target.closest('[data-qty-inc]');
        const decBtn = e.target.closest('[data-qty-dec]');
        const rmBtn  = e.target.closest('[data-line-remove]');
        const editBtn = e.target.closest('[data-variant-edit]');
        const optBtn = e.target.closest('[data-line-opt]');
        const confirmBtn = e.target.closest('[data-variant-confirm]');
        const cancelBtn = e.target.closest('[data-variant-cancel]');
        const line = e.target.closest('[data-line-key]');
        if (!line) return;
        const key = line.dataset.lineKey;
        if (incBtn) changeLineQty(key, +1);
        else if (decBtn) changeLineQty(key, -1);
        else if (rmBtn) removeLine(key);
        else if (editBtn) openLineVariantEditor(line);
        else if (cancelBtn) {
          const editor = cancelBtn.closest('[data-variant-editor]');
          if (editor) {
            restoreLineFromCache(line);
            editor.hidden = true;
            editor.innerHTML = '';
          }
        }
        else if (confirmBtn) {
          if (confirmBtn.hasAttribute('disabled')) return;
          const editor = confirmBtn.closest('[data-variant-editor]');
          if (!editor || !editor._product) return;
          const v = editor._product.variants.find(vv => vv.options.every((o, j) => o === editor._selected[j]));
          if (!v || v.id === parseInt(editor.dataset.currentVariantId, 10)) return;
          // DOM already shows the preview. Commit to cartCache so totals stay consistent,
          // then sync to server in background with a loading indicator on the row.
          const cachedLine = cartCache && cartCache.items.find(l => String(l.key) === String(key));
          if (cachedLine) {
            cachedLine.variant_id = v.id;
            cachedLine.variant_title = v.title;
            cachedLine.final_price = v.price;
            cachedLine.price = v.price;
            cachedLine.final_line_price = v.price * cachedLine.quantity;
            cachedLine.line_price = cachedLine.final_line_price;
            if (v.featured_image && v.featured_image.src) cachedLine.image = v.featured_image.src;
            cartCache.total_price = cartCache.items.reduce((s, l) => s + l.final_line_price, 0);
          }
          line.dataset.lineVariantId = v.id;
          line.classList.add('syncing');
          editor.hidden = true;
          editor.innerHTML = '';
          switchLineVariant(key, v.id).finally(() => line.classList.remove('syncing'));
        }
        else if (optBtn) {
          const editor = optBtn.closest('[data-variant-editor]');
          if (!editor || !editor._product) return;
          const i = parseInt(optBtn.dataset.lineOpt, 10);
          editor._selected[i] = optBtn.dataset.lineVal;
          renderVariantEditor(editor, editor._product, editor._selected, parseInt(editor.dataset.currentVariantId, 10));
          const candidate = editor._product.variants.find(vv => vv.options.every((o, j) => o === editor._selected[j]));
          if (candidate) applyVariantPreview(line, candidate);
          else restoreLineFromCache(line);
        }
      });
    }

    // Quick-add triggers (delegated — cards may be re-rendered)
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-quick-add-trigger]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = trigger.dataset.productHandle;
      if (!handle) return;
      openQuickAdd();
      loadQuickAdd(handle, trigger.dataset);
    });
    // Upsell carousel (delegated — present in the drawer footer and on the cart page,
    // both re-rendered on every cart change)
    document.addEventListener('click', e => {
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) { e.preventDefault(); upsellAdd(addBtn); return; }
      const arrow = e.target.closest('[data-upsell-prev], [data-upsell-next]');
      if (arrow) {
        e.preventDefault();
        const wrap = arrow.closest('[data-upsell]');
        const track = wrap && wrap.querySelector('[data-upsell-track]');
        if (!track) return;
        const item = track.querySelector('.tp-upsell__item');
        const step = item ? item.offsetWidth + 10 : track.clientWidth * 0.8;
        track.scrollBy({ left: arrow.hasAttribute('data-upsell-prev') ? -step : step, behavior: 'smooth' });
      }
    });

    // Quick-add close
    document.querySelectorAll('[data-quick-add-close]').forEach(el => el.addEventListener('click', closeQuickAdd));
    if (qaScrim()) qaScrim().addEventListener('click', closeQuickAdd);
    // Quick-add option clicks (delegated — body is re-rendered on every select)
    const qaBody = document.getElementById('quick-add-body');
    if (qaBody) {
      qaBody.addEventListener('click', e => {
        const btn = e.target.closest('[data-qa-option]');
        if (!btn) return;
        const idx = parseInt(btn.dataset.qaOption, 10);
        qaSelected[idx] = btn.dataset.qaValue;
        renderQuickAdd();
      });
    }

    // Keyboard
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') { closeQuickAdd(); closeDrawer(); closeMobileNav(); closeSearch(); closeNavDropdowns(); }
    });

    // Mobile nav
    document.querySelectorAll('[data-mobile-nav-open]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); openMobileNav(); }));
    document.querySelectorAll('[data-mobile-nav-close]').forEach(el => el.addEventListener('click', closeMobileNav));

    // Mobile submenu expand / collapse
    const mNav = document.querySelector('#MobileNav .m-nav');
    if (mNav) {
      mNav.addEventListener('click', e => {
        const btn = e.target.closest('[data-mobile-submenu-toggle]');
        if (!btn) return;
        e.preventDefault();
        const wrapper = btn.closest('.m-nav__item--has-children, .m-nav__sub-item--has-children');
        if (!wrapper) return;
        // Class chứ không phải attribute `hidden`: display:none không transition được, nên
        // chiều cao được animate bằng grid-template-rows 0fr→1fr trên .m-nav__sub-wrap.
        // aria-expanded ở đây là thật — JS nắm trạng thái, khác desktop nơi :hover nắm.
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        wrapper.classList.toggle('is-open', !isOpen);
        // Đóng cha thì đóng luôn con. `visibility` được THỪA KẾ, nên một tầng 3 còn .is-open
        // bên trong tầng 2 đã đóng sẽ tự đặt lại `visible` — link vẫn focus được trong khối
        // cao 0px và overflow:hidden, tức bẫy bàn phím: focus ở chỗ không ai thấy.
        if (isOpen) {
          wrapper.querySelectorAll('.is-open').forEach(el => el.classList.remove('is-open'));
          wrapper.querySelectorAll('[data-mobile-submenu-toggle]').forEach(b => {
            if (b !== btn) b.setAttribute('aria-expanded', 'false');
          });
        }
      });
    }

    // Desktop nav dropdown — chỉ thiết bị cảm ứng cần tới đây
    const dNav = document.querySelector('.tp-header nav.tp-nav');
    if (dNav) {
      dNav.addEventListener('click', e => {
        const parent = e.target.closest('[data-nav-parent]');
        if (!parent || !window.matchMedia('(hover: none)').matches) return;
        const item = parent.closest('.tp-nav__item--has-children');
        if (!item) return;
        const wasOpen = item.classList.contains('is-open');
        closeNavDropdowns();
        // Đang đóng thì tap này chỉ để mở, chặn điều hướng. Tap thứ hai vào đúng mục đó
        // không vào nhánh này nữa (wasOpen = true) nên link cha chạy bình thường.
        if (!wasOpen) { e.preventDefault(); item.classList.add('is-open'); }
      });
      // Chạy sau listener trên (bubble), và click bên trong item thì closest() khác null nên không đóng.
      document.addEventListener('click', e => {
        if (!e.target.closest('.tp-nav__item--has-children')) closeNavDropdowns();
      });
      // Đổi loại con trỏ (cắm chuột vào iPad, quay màn) không để panel treo ở trạng thái mở.
      const mqHover = window.matchMedia('(hover: none)');
      if (mqHover.addEventListener) mqHover.addEventListener('change', closeNavDropdowns);
    }

    // Inline search
    document.querySelectorAll('[data-search-open]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); openSearch(); }));
    document.querySelectorAll('[data-search-close]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); closeSearch(); }));
    const searchInput = document.querySelector('#tp-search-panel input[type="search"]');
    if (searchInput) {
      searchInput.addEventListener('input', e => {
        clearTimeout(searchTimer);
        const val = e.target.value;
        searchTimer = setTimeout(() => runSearch(val), 180);
      });
    }
    const searchForm = document.querySelector('#tp-search-panel form');
    if (searchForm) {
      searchForm.addEventListener('submit', e => {
        // Let it submit to /search for the full results page.
      });
    }

    // Intercept add-to-cart forms
    document.addEventListener('submit', e => {
      const form = e.target.closest('form[data-cart-add]');
      if (!form) return;
      e.preventDefault();
      addToCart(form);
    });

    // Discount code controls. Delegated because the drawer footer is rebuilt from
    // scratch on every cart change, and because the cart page renders its own copy
    // of the same block server-side.
    document.addEventListener('click', e => {
      if (!e.target || !e.target.closest) return;

      const toggle = e.target.closest('[data-discount-toggle]');
      if (toggle) {
        const form = toggle.closest('[data-discount-block]');
        const field = form && form.querySelector('[data-discount-form]');
        if (!field) return;
        discountFieldOpen = field.hidden;
        field.hidden = !discountFieldOpen;
        toggle.setAttribute('aria-expanded', discountFieldOpen ? 'true' : 'false');
        if (discountFieldOpen) {
          const input = field.querySelector('[data-discount-input]');
          if (input) input.focus();
        }
        return;
      }

      const apply = e.target.closest('[data-discount-apply]');
      if (apply) {
        const block = apply.closest('[data-discount-block]');
        const input = block && block.querySelector('[data-discount-input]');
        applyDiscount(input ? input.value : '');
        return;
      }

      const remove = e.target.closest('[data-discount-remove]');
      if (remove) removeDiscount(remove.getAttribute('data-discount-remove'));
    });

    // On the cart page the discount field sits inside <form action="/cart">, so a
    // bare Enter would submit the quantity form and reload the page instead of
    // applying the code. Claim the key before that can happen.
    document.addEventListener('keydown', e => {
      if (e.key !== 'Enter') return;
      if (!e.target || !e.target.closest) return;
      const input = e.target.closest('[data-discount-input]');
      if (!input) return;
      e.preventDefault();
      applyDiscount(input.value);
    });

    // Drop a subtle shadow on the header once the user scrolls
    const headerEl = document.querySelector('.tp-header');
    if (headerEl) {
      const onScroll = () => {
        if (window.scrollY > 6) headerEl.classList.add('scrolled');
        else headerEl.classList.remove('scrolled');
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // Warm the cart cache so the drawer can open without waiting
    fetchCart().then(cart => { cartCache = cart; renderCart(cart); }).catch(() => {});

    // Record + render "Recently viewed" once the consent API is ready, and re-run if the
    // visitor changes their choice (no reload needed). See initRecentlyViewed for gating.
    rvBootstrapRecentlyViewed();
  }

  // ─── Recently viewed ───────────────────────────────────────
  // Personal browsing history, so it lives in localStorage rather than on the server.
  // The host section (sections/recently-viewed.liquid) renders an empty grid; here we
  // record the current product and hydrate the grid from stored handles via the Section
  // Rendering API (one /products/{handle}?section_id=recently-viewed-card fetch per card,
  // mirroring how refreshUpsell() pulls in the cart-upsell carousel).
  //
  // Consent: localStorage is covered by UK PECR / GDPR the same as cookies, and "recently
  // viewed" is a functional/preferences feature (not strictly necessary). So we gate it on
  // Shopify's consent API — preferencesProcessingAllowed(). When no consent API is present
  // (merchant runs no banner), it behaves as a normal functional feature.
  const RV_KEY = 'tp:recently-viewed';
  const RV_MAX_STORED = 12;

  function rvRead() {
    try {
      const v = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      return Array.isArray(v) ? v.filter(h => typeof h === 'string' && h) : [];
    } catch (e) { return []; }
  }
  function rvWrite(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) { /* private mode / full — skip */ }
  }

  async function renderRecentlyViewed(host, handles) {
    const grid = host.querySelector('[data-rv-grid]');
    if (!grid || !handles.length) return;
    const limit = parseInt(host.getAttribute('data-rv-limit'), 10) || 4;
    const show = handles.slice(0, limit);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
    const base = root.replace(/\/$/, '');
    const cards = await Promise.all(show.map(async h => {
      try {
        const r = await fetch(base + '/products/' + encodeURIComponent(h) + '?section_id=recently-viewed-card', { headers: { Accept: 'text/html' } });
        if (!r.ok) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = await r.text();
        const card = tmp.querySelector('.product-card');
        return card ? card.outerHTML : '';
      } catch (e) { return ''; }
    }));
    const html = cards.filter(Boolean).join('');
    if (!html) return;
    grid.innerHTML = html;
    host.removeAttribute('hidden');
    // The grid was empty (and the host display:none) until this moment, so the carousel's
    // first measurement saw nothing to scroll and hid its arrows. Re-measure now.
    document.dispatchEvent(new CustomEvent('tp:carousel-refresh'));
  }

  // True when we may store/show recently-viewed. No consent API present (no banner
  // configured) → treat as a normal functional feature and allow. API present → respect
  // the visitor's choice for the "preferences" category.
  function rvConsentAllowed() {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.preferencesProcessingAllowed === 'function') {
        return cp.preferencesProcessingAllowed() === true;
      }
    } catch (e) { /* fall through */ }
    return true;
  }

  function initRecentlyViewed() {
    const host = document.querySelector('[data-recently-viewed]');

    // No consent (declined, revoked, or not yet given) → store nothing, show nothing,
    // and clear anything kept from an earlier consented session.
    if (!rvConsentAllowed()) {
      if (host) {
        host.setAttribute('hidden', '');
        const g = host.querySelector('[data-rv-grid]');
        if (g) g.innerHTML = '';
      }
      try { localStorage.removeItem(RV_KEY); } catch (e) {}
      return;
    }

    const current = host ? host.getAttribute('data-recently-viewed') : '';
    const list = rvRead();

    // Show the products viewed before this one (never the current product itself)
    if (host) renderRecentlyViewed(host, list.filter(h => h !== current));

    // Record the current product, most-recent-first, de-duped, capped
    if (current) {
      const next = [current].concat(list.filter(h => h !== current)).slice(0, RV_MAX_STORED);
      rvWrite(next);
    }
  }

  // Wait for the consent API to load (so we don't record in the brief window before a
  // banner app initialises), run once, then re-run whenever the visitor's consent changes.
  function rvBootstrapRecentlyViewed() {
    if (!document.querySelector('[data-recently-viewed]')) return;
    const start = function () {
      initRecentlyViewed();
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.subscribe === 'function') {
          cp.subscribe('visitorConsentCollected', initRecentlyViewed);
        } else {
          document.addEventListener('visitorConsentCollected', initRecentlyViewed);
        }
      } catch (e) {
        document.addEventListener('visitorConsentCollected', initRecentlyViewed);
      }
    };
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) { start(); return; }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], start);
        return;
      }
    } catch (e) { /* fall through to start without the API */ }
    start();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.TP = {
    openDrawer, closeDrawer, fetchCart, renderCart, addToCart, toast,
    openMobileNav, closeMobileNav, openSearch, closeSearch,
    openQuickAdd, closeQuickAdd, loadQuickAdd, addItems: addItemsToCart,
  };
})();

/* ---------------------------------------------------------------------------
   Product-card hover image (Theme settings → Commerce → Product cards).

   Self-contained: the markup ships with no `src`, so with this file absent or the
   setting off the second image is simply never fetched. Cost is zero until a real
   pointer enters a card.

   Delegated `pointerover` rather than a per-card `mouseenter`: the grid is re-rendered
   by the collection filters, the search panel and recently-viewed, and mouseenter does
   not bubble so it cannot be delegated at all. One passive listener covers every card
   on every template.
--------------------------------------------------------------------------- */
(function () {
  // Touch and coarse pointers never fire the hover state, so attaching anything there
  // would be pure overhead — and would risk a phantom fetch on a tap-through.
  try {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  } catch (e) { return; }

  document.addEventListener('pointerover', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var card = t.closest('.product-card');
    if (!card) return;
    var img = card.querySelector('img.card-img--hover[data-hover-src]');
    if (!img) return;

    // Fetch once per card, ever. The attribute is removed rather than flagged so a
    // second hover cannot re-enter this branch at all.
    var src = img.getAttribute('data-hover-src');
    img.removeAttribute('data-hover-src');
    img.src = src;

    var reveal = function () { img.classList.add('is-ready'); };
    if (typeof img.decode === 'function') {
      // Only fade in a fully decoded image: a Shopify CDN derivative that has never been
      // generated can hang on its first request, and revealing early would flash blank.
      // On failure drop the element instead — the featured image underneath is a correct
      // card on its own, a broken-image icon is not.
      img.decode().then(reveal).catch(function () { img.remove(); });
    } else {
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', function () { img.remove(); }, { once: true });
    }
  }, { passive: true });
})();

/* ---------------------------------------------------------------------------
   Announcement bar — keep the message on a single line.

   The CSS does the real work: .tp-topbar .ship-line is `white-space: nowrap` with a
   fluid clamp that scales the type with the viewport. But a clamp can only be tuned to
   one string, and the message is a Customiser setting — a merchant who writes something
   longer than the shipped copy would get it clipped by the `text-overflow: ellipsis`
   failsafe. So measure what is actually on screen and shrink the type the rest of the
   way, down to 80% of the clamp. Below that the copy is simply too long for a phone and
   the ellipsis is the honest answer.

   Cheap by construction: on the common case — the clamp already fits the copy — it reads
   two properties, finds nothing to do and stops.
--------------------------------------------------------------------------- */
(function () {
  var MIN_SCALE = 0.8;

  function fit() {
    var lines = document.querySelectorAll('.tp-topbar .ship-line');
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      // Always start from the unscaled size, or repeated calls (resize, font swap) would
      // compound their own corrections and walk the text down to nothing.
      line.style.setProperty('--tp-topbar-fit', '1');
      if (!line.clientWidth) continue;
      // Iterated, not solved in one pass: the 8px side padding is inside clientWidth but
      // does not scale with the type, and glyph advances round rather than shrinking
      // cleanly, so the single `avail / need` ratio undershoots at the narrow end — it
      // left a 320px viewport still 4px over. In practice the CSS clamp already fits and
      // this breaks out on the first check.
      var scale = 1;
      for (var pass = 0; pass < 8; pass++) {
        var avail = line.clientWidth;
        var need = line.scrollWidth;
        if (need <= avail) break;
        // scrollWidth rounds up, so take a further 1% off rather than landing exactly on
        // the boundary and letting the ellipsis appear anyway.
        var next = Math.max(MIN_SCALE, scale * ((avail / need) - 0.01));
        if (next >= scale) break; // at the floor, or no longer converging
        scale = next;
        line.style.setProperty('--tp-topbar-fit', String(scale));
      }
    }
  }

  fit();
  // The bar renders in the fallback face first; DM Sans is wider in places, so re-fit
  // once it has swapped in.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit).catch(function () {});

  var pending = 0;
  window.addEventListener('resize', function () {
    if (pending) return;
    pending = requestAnimationFrame(function () { pending = 0; fit(); });
  }, { passive: true });

  // Editing the message in the Theme Customiser re-renders the section.
  document.addEventListener('shopify:section:load', fit);
})();

// ============================================================
// Shared product carousel — [data-carousel] / [data-carousel-track]
//
// Used by Featured Kits (home), "You might also like" and "Recently viewed" (PDP).
// Card widths are pure CSS (see .tp-carousel in theme.css); this only drives the arrows
// and hides them when there is nothing to scroll.
//
// Re-runs on `tp:carousel-refresh`, which recently-viewed fires after it injects its
// cards — that grid starts empty and is hydrated from localStorage, so its scrollWidth
// is meaningless until then. Also re-runs on `shopify:section:load` for the Customiser.
// Init is idempotent (data-carousel-ready), so a refresh never double-binds.
// ============================================================
(function () {
  'use strict';

  function setup(car) {
    var track = car.querySelector('[data-carousel-track]');
    var prev = car.querySelector('[data-carousel-prev]');
    var next = car.querySelector('[data-carousel-next]');
    if (!track) return null;

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      // 1px, not 0: scrollWidth/clientWidth round to integers, so a track whose cards fit
      // exactly still reports a stray fractional pixel of overflow.
      if (max <= 1) {
        car.classList.add('no-nav');
        if (prev) prev.setAttribute('disabled', '');
        if (next) next.setAttribute('disabled', '');
        return;
      }
      car.classList.remove('no-nav');
      if (prev) {
        if (track.scrollLeft <= 1) prev.setAttribute('disabled', '');
        else prev.removeAttribute('disabled');
      }
      if (next) {
        if (track.scrollLeft >= max - 1) next.setAttribute('disabled', '');
        else next.removeAttribute('disabled');
      }
    }

    // Step by whole cards, never by a fixed fraction of the viewport: with a fractional
    // --tp-carousel-visible the card that was peeking has to land flush at the left edge,
    // otherwise every press leaves the strip a little more misaligned.
    function step(dir) {
      var first = track.firstElementChild;
      if (!first) return;
      var cardW = first.getBoundingClientRect().width;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var perView = Math.max(1, Math.floor((track.clientWidth + gap) / (cardW + gap)));
      track.scrollBy({ left: dir * perView * (cardW + gap), behavior: 'smooth' });
    }

    if (prev && !prev.hasAttribute('data-carousel-bound')) {
      prev.setAttribute('data-carousel-bound', '');
      prev.addEventListener('click', function () { step(-1); });
    }
    if (next && !next.hasAttribute('data-carousel-bound')) {
      next.setAttribute('data-carousel-bound', '');
      next.addEventListener('click', function () { step(1); });
    }
    if (car.getAttribute('data-carousel-ready') !== '1') {
      car.setAttribute('data-carousel-ready', '1');
      track.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
    }
    return update;
  }

  var updaters = [];

  function init() {
    updaters = [];
    Array.prototype.forEach.call(document.querySelectorAll('[data-carousel]'), function (car) {
      var up = setup(car);
      if (up) { updaters.push(up); up(); }
    });
  }

  function refreshAll() {
    updaters.forEach(function (up) { up(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Card images have no width until they decode, so the first measurement can read a
  // too-narrow scrollWidth and wrongly hide the arrows.
  window.addEventListener('load', refreshAll);
  window.addEventListener('resize', function () { requestAnimationFrame(refreshAll); });
  document.addEventListener('tp:carousel-refresh', init);
  document.addEventListener('shopify:section:load', init);
})();

/* ---------------------------------------------------------------------------
   Product-card swipe gallery — dots only.

   The swipe itself is the browser's: `.card-media` is a scroll-snap container
   (see theme.css.liquid), so there is nothing here to drive it, and nothing that
   can fight the page's own scrolling. All this does is mark which slide is
   showing.

   Bound once, in the capture phase, rather than per card: scroll events do not
   bubble, but they can be captured — and the collection page replaces the whole
   contents of its grid when the sub-collection buttons are used, so anything
   attached per card would have to be re-attached on every filter.
--------------------------------------------------------------------------- */
(function () {
  var queued = false;
  var pending = [];

  // Slides past the first carry their URL in data-slide-src and get it here, once, the moment
  // the shopper touches the strip. `loading="lazy"` is not enough on its own: measured
  // 2026-09-22, Chrome fetched slide 2 of every card at page load even though it sits a full
  // card-width outside the viewport behind `overflow`. touchstart fires before the finger has
  // moved, so the request is already in flight while the snap animation plays.
  function prime(media) {
    if (media.getAttribute('data-primed')) return;
    media.setAttribute('data-primed', '1');
    var slides = media.querySelectorAll('img[data-slide-src]');
    for (var n = 0; n < slides.length; n++) {
      var src = slides[n].getAttribute('data-slide-src');
      slides[n].removeAttribute('data-slide-src');
      slides[n].src = src;
    }
  }

  function stripFrom(node) {
    return node && node.closest ? node.closest('[data-card-media]') : null;
  }

  document.addEventListener('touchstart', function (e) {
    var media = stripFrom(e.target);
    if (media) prime(media);
  }, { capture: true, passive: true });

  function paint() {
    queued = false;
    pending.forEach(function (media) {
      var dots = media.parentElement && media.parentElement.querySelector('[data-card-dots]');
      if (!dots || !media.clientWidth) return;
      // Round, not floor: a snap can settle a fraction of a pixel short of the slide.
      var i = Math.round(media.scrollLeft / media.clientWidth);
      var spans = dots.children;
      if (i < 0) i = 0;
      if (i > spans.length - 1) i = spans.length - 1;
      for (var n = 0; n < spans.length; n++) {
        spans[n].classList.toggle('on', n === i);
      }
    });
    pending = [];
  }

  document.addEventListener('scroll', function (e) {
    var media = e.target;
    if (!media || media.nodeType !== 1 || !media.hasAttribute || !media.hasAttribute('data-card-media')) return;
    // Covers a trackpad or a dragged scrollbar on a narrow desktop window, where no touch fires.
    prime(media);
    if (pending.indexOf(media) === -1) pending.push(media);
    if (queued) return;
    queued = true;
    requestAnimationFrame(paint);
  }, true);
})();
